import { BundleItem, RedeemCodeResponse } from 'mytours-read-api-typescript'
import { Collection } from '../api/types'
import { CodeStatus } from '../lockapp/types'
import { BundleOffer, RedeemedBundles } from './types'

/**
 * Whether a redeemed bundle unlocks this collection.
 *
 * Asks the collection first: `available_in_bundles` is its own record of every bundle it is sold in, so
 * it needs no bundle fetched and no catalogue consulted. That matters because the reverse index below
 * reads contents the same-region IAP catalogue supplied, and that catalogue can never name a guide
 * client's bundle in another region — the case bundle locks exist for. Without this, redeeming a
 * cross-region bundle left every collection inside it locked.
 *
 * The reverse index stays as a fallback, for a collection payload cached before the field existed.
 *
 * Reads the whole of `available_in_bundles`, never the offered subset: a bundle the app never advertised
 * still opens the collection once it has been redeemed.
 */
export function isCollectionInRedeemedBundle(
	collectionId: number,
	redeemedBundles: RedeemedBundles,
	collection: Collection | undefined,
): boolean {
	const offers = collection?.available_in_bundles ?? []
	if (offers.some(bundle => bundle.id !== undefined && redeemedBundles[bundle.id]?.hasUnlockedBundle)) {
		return true
	}

	for (const bundleId in redeemedBundles) {
		if (!Object.prototype.hasOwnProperty.call(redeemedBundles, bundleId)) {
			continue
		}
		const entry = redeemedBundles[bundleId]
		if (!entry?.hasUnlockedBundle || !entry.items) {
			continue
		}
		const hit = entry.items.some(
			item => item.item_type === BundleItem.ItemTypeEnum.Collection && item.item_id === collectionId,
		)
		if (hit) {
			return true
		}
	}
	return false
}

/**
 * The bundles to put in front of a reader as a way into `collection`, out of the wider set that opens it.
 *
 * An absent flag reads as offered: that is the default, and a payload cached before the field existed
 * carries none.
 */
export function offeredBundlesFor(collection: { available_in_bundles?: BundleOffer[] } | undefined): BundleOffer[] {
	return (collection?.available_in_bundles ?? []).filter(bundle => bundle.offer_unlock !== false)
}

/**
 * Whether a bundle still gates `collection`.
 *
 * `bundle_locked` only says a bundle gates it at all. Any one way in is enough to open it, so this is
 * also false once a bundle holding it is redeemed, and once the collection's own code or price is
 * satisfied — which `lockCollection` reports as `valid`. Without that last term, redeeming the code
 * clears the collection's own gate and the bundle gate closes behind it, so entering a valid code
 * appears to lock the collection rather than open it.
 */
export function isCollectionBundleLocked(
	collection: { bundle_locked?: boolean } | undefined,
	unlockedViaBundle: boolean | undefined,
	lockCollection: CodeStatus | undefined,
): boolean {
	return (
		!!collection?.bundle_locked && !unlockedViaBundle && lockCollection?.status !== RedeemCodeResponse.StatusEnum.Valid
	)
}
